import { Check } from 'lucide-react';
import { cn } from '@/lib/cn';

export type StepState = 'done' | 'current' | 'todo';

export interface Step {
  id: string;
  label: string;
  state: StepState;
}

/** Spoken equivalent of the visual marker, which is aria-hidden. */
const STATE_LABEL: Record<StepState, string> = {
  done: 'выполнено',
  current: 'текущий шаг',
  todo: 'не начат',
};

/**
 * Horizontal on desktop, vertical below 640px. The connector is a hairline, and
 * the only filled marker is the completed one — the current step is outlined so
 * the eye lands on "where I am", not on "what I've finished".
 */
export function Stepper({ steps }: { steps: Step[] }) {
  const currentIndex = steps.findIndex((step) => step.state === 'current');
  const position = currentIndex === -1 ? steps.length : currentIndex + 1;

  return (
    <nav aria-label="Ход верификации">
      <p className="sr-only">
        Шаг {position} из {steps.length}
      </p>

      <ol className="flex flex-col gap-0 sm:flex-row sm:items-start">
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;

          return (
            <li
              key={step.id}
              // The marker that carries done/current/todo visually is
              // aria-hidden, so the state is restated here for assistive tech.
              aria-current={step.state === 'current' ? 'step' : undefined}
              className={cn('flex gap-3 sm:flex-1 sm:flex-col sm:gap-2', !isLast && 'sm:pr-4')}
            >
              {/* Marker + connector */}
              <div className="flex shrink-0 flex-col items-center sm:w-full sm:flex-row">
                <Marker state={step.state} index={index} />
                {!isLast ? (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'my-1 w-px flex-1 sm:my-0 sm:ml-3 sm:h-px sm:w-auto sm:flex-1',
                      step.state === 'done' ? 'bg-fg-muted' : 'bg-line',
                    )}
                  />
                ) : null}
              </div>

              <div className={cn('min-w-0', !isLast && 'pb-5 sm:pb-0')}>
                <p className="eyebrow">Шаг {index + 1}</p>
                <p
                  className={cn(
                    'mt-0.5 text-base leading-snug',
                    step.state === 'current'
                      ? 'font-semibold text-fg'
                      : step.state === 'done'
                        ? 'font-medium text-fg-secondary'
                        : 'font-normal text-fg-muted',
                  )}
                >
                  {step.label}
                  <span className="sr-only">{` — ${STATE_LABEL[step.state]}`}</span>
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function Marker({ state, index }: { state: StepState; index: number }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'num flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-2xs font-semibold',
        state === 'done' && 'border-fg bg-inverse text-fg-inverse',
        state === 'current' && 'border-fg bg-surface text-fg',
        state === 'todo' && 'border-line-strong bg-surface text-fg-muted',
      )}
    >
      {state === 'done' ? <Check size={12} strokeWidth={1.5} /> : index + 1}
    </span>
  );
}
